import { useMutation, UseMutationResult } from '@tanstack/react-query'
import { gql } from 'graphql-request'

import {
  consentVersionForSource,
  RECORD_SMS_OPT_IN_MUTATION,
  type RecordSmsOptInResult,
  type SmsOptInInput,
  type SmsOptInSource,
} from 'lib/utils/smsOptIn'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'
import { getRudderstackAnonymousIdWithTimeout } from '@/hooks/useRudderstackAnalytics'
import useSessionStore from '@/stores/useSessionStore'

const RECORD_SMS_OPT_IN = gql`
  ${RECORD_SMS_OPT_IN_MUTATION}
`

export interface RecordSmsOptInVariables {
  /** E.164 '+1XXXXXXXXXX' — normalize with normalizeUsPhone before calling. */
  phone: string
  smsSource: SmsOptInSource
  email?: string
}

const GENERIC_ERROR = 'Something went wrong. Please try again.'

// Records an SMS marketing opt-in through the shared capture path (GSE-12082).
// Brand comes from the x-brand-id header; anonymousId goes in the input so the
// backend can stitch the RudderStack identity (bounded wait: an early submit
// can beat the SDK load). No reCAPTCHA on this path by design (the backend
// defers abuse protection to the WAF).
//
// Identity (GSE-12330): externalId carries the signed-in user id — the same id
// RudderStack identifies on, which is what the backend sees as Braze's
// external_id. Read through getState() rather than a selector so the sign-up
// flow picks it up in the same tick: ClientPortal calls setAuth() and then
// recordSignUpSmsOptIn() back to back, and zustand's set is synchronous. That
// request still goes out on the pre-login memoized client with an empty
// x-user-token, so externalId is the only identity it carries.
//
// A logged-out opt-in sends no id at all, and nothing is kept on this browser
// for later: the backend ties the record to whichever account signs up or logs
// in with the same phone number, the first time that account reads its own
// subscription (SmsSubscriptionStateBootstrap does so on every logged-in load).
const useRecordSmsOptInApi = (): UseMutationResult<
  RecordSmsOptInResult,
  Error,
  RecordSmsOptInVariables
> => {
  const client = useGraphQLClient()

  const mutationFn = async ({
    phone,
    smsSource,
    email,
  }: RecordSmsOptInVariables): Promise<RecordSmsOptInResult> => {
    const userId = useSessionStore.getState().userId
    const input: SmsOptInInput = {
      phone,
      smsSource,
      email,
      consentTextVersion: consentVersionForSource(smsSource),
      anonymousId: await getRudderstackAnonymousIdWithTimeout(1000),
      externalId: userId ?? undefined,
    }

    const result = await client.request<{
      recordSmsOptIn: RecordSmsOptInResult
    }>(RECORD_SMS_OPT_IN, { input })

    const payload = result?.recordSmsOptIn
    if (!payload?.success) {
      throw new Error(payload?.errors?.[0]?.message || payload?.message || GENERIC_ERROR)
    }

    return payload
  }

  return useMutation({ mutationFn, retry: false })
}

export default useRecordSmsOptInApi
